import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Input, Select, Space, message, Tag, DatePicker } from 'antd';
import {
  SearchOutlined,
  ClearOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { makePostCall } from '../../utils/helper';
import DateFormatter from '../../components/DateFormatter';
import './integration-logs.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

const IntegrationLogs = ({ logType }) => {
  const [logs, setLogs] = useState({ data: [], total: 0, loading: false, error: null });
  const requestIdRef = useRef(0);

  // Filters
  const [searchText, setSearchText] = useState(''); // for Accession Number
  const [patientId, setPatientId] = useState('');
  const [centerName, setCenterName] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState([null, null]);

  // Active/applied filters for the query
  const [appliedFilters, setAppliedFilters] = useState({
    acc_no: '',
    patient_id: '',
    facility: '',
    status: 'all',
    from_date: null,
    to_date: null,
  });

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const fetchLogs = useCallback(async (filters = appliedFilters, page = pagination.current, pageSize = pagination.pageSize) => {
    const currentReqId = ++requestIdRef.current;
    setLogs(prev => ({ ...prev, loading: true, error: null }));
    try {
      const payload = {
        page,
        limit: pageSize,
        type: logType,
      };

      if (filters.status && filters.status !== 'all') {
        payload.status = filters.status;
      }
      if (filters.acc_no) {
        payload.acc_no = filters.acc_no;
      }
      if (filters.patient_id) {
        payload.patient_id = filters.patient_id;
      }
      if (filters.facility) {
        payload.facility = filters.facility;
      }
      if (filters.from_date && filters.to_date) {
        payload.from_date = filters.from_date;
        payload.to_date = filters.to_date;
      }

      const response = await makePostCall('/hl7-logs', payload);
      // Discard stale responses from previous requests or tabs
      if (currentReqId !== requestIdRef.current) return;

      if (response.data.success) {
        setLogs({
          data: response.data.data.logs || [],
          total: response.data.data.total || 0,
          loading: false,
          error: null,
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch logs');
      }
    } catch (error) {
      if (currentReqId !== requestIdRef.current) return;
      setLogs({
        data: [],
        total: 0,
        loading: false,
        error: error.message || 'Failed to fetch logs',
      });
      message.error('Failed to fetch logs');
    }
  }, [logType, appliedFilters, pagination.current, pagination.pageSize]);

  // Reset filters and fetch when logType tab changes
  useEffect(() => {
    setSearchText('');
    setPatientId('');
    setCenterName('');
    setStatusFilter('all');
    setDateRange([null, null]);
    const emptyFilters = {
      acc_no: '',
      patient_id: '',
      facility: '',
      status: 'all',
      from_date: null,
      to_date: null,
    };
    setAppliedFilters(emptyFilters);
    setPagination({ current: 1, pageSize: 10 });
    fetchLogs(emptyFilters, 1, 10);
  }, [logType]);

  const handleSearch = () => {
    const nextFilters = {
      acc_no: searchText.trim(),
      patient_id: patientId.trim(),
      facility: centerName.trim(),
      status: statusFilter,
      from_date: dateRange[0] && dateRange[1] ? dateRange[0].startOf('day').toISOString() : null,
      to_date: dateRange[0] && dateRange[1] ? dateRange[1].endOf('day').toISOString() : null,
    };
    setAppliedFilters(nextFilters);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchLogs(nextFilters, 1, pagination.pageSize);
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    fetchLogs(appliedFilters, newPagination.current, newPagination.pageSize);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setPatientId('');
    setCenterName('');
    setStatusFilter('all');
    setDateRange([null, null]);
    const emptyFilters = {
      acc_no: '',
      patient_id: '',
      facility: '',
      status: 'all',
      from_date: null,
      to_date: null,
    };
    setAppliedFilters(emptyFilters);
    setPagination({ current: 1, pageSize: 10 });
    fetchLogs(emptyFilters, 1, 10);
  };
  const handleRefresh = () => {
    fetchLogs(appliedFilters, pagination.current, pagination.pageSize);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DELIVERED': return 'green';
      case 'PENDING': return 'blue';
      case 'FAILED': return 'red';
      case 'REJECTED': return 'orange';
      case 'NO_ROUTE': return 'default';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'hol_type',
      key: 'hol_type',
      render: (type) => <Tag color={type === 'MWL' ? 'cyan' : 'purple'}>{type}</Tag>
    },
    {
      title: 'Accession No',
      dataIndex: 'hol_acc_no',
      key: 'hol_acc_no',
    },
    {
      title: 'Center',
      dataIndex: 'hol_facility',
      key: 'hol_facility',
    },
    {
      title: 'Destination',
      dataIndex: 'hol_destination',
      key: 'hol_destination',
    },
    {
      title: 'Status',
      dataIndex: 'hol_status',
      key: 'hol_status',
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>
    },
    {
      title: 'Attempts',
      dataIndex: 'hol_attempts',
      key: 'hol_attempts',
    },
    {
      title: 'Last Error',
      dataIndex: 'hol_last_error',
      key: 'hol_last_error',
      render: (err) => err ? <span style={{ color: 'red' }} title={err}>{err.length > 30 ? err.substring(0, 30) + '...' : err}</span> : '—'
    },
    {
      title: 'Created At',
      dataIndex: 'hol_created_at',
      key: 'hol_created_at',
      render: (date) => <DateFormatter date={date} format="DD MMM YYYY HH:mm" />
    },
    {
      title: 'Delivered At',
      dataIndex: 'hol_delivered_at',
      key: 'hol_delivered_at',
      render: (date) => date ? <DateFormatter date={date} format="DD MMM YYYY HH:mm" /> : '—'
    }
  ];

  return (
    <div className="admin-container integration-logs-container">
      <div className="admin-header">
        <h2>{logType === 'MWL' ? 'MWL Forwarding Logs' : 'HIS ORU Sign-off Logs'}</h2>
      </div>

      <div className="admin-filters">
        <Space size="middle" wrap style={{ rowGap: 12 }}>
          <Input
            placeholder="Accession Number"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 180 }}
            allowClear
          />

          <Input
            placeholder="Patient ID"
            prefix={<SearchOutlined />}
            value={patientId}
            onChange={(e) => setPatientId(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 180 }}
            allowClear
          />

          <Input
            placeholder="Center Name/Code"
            prefix={<SearchOutlined />}
            value={centerName}
            onChange={(e) => setCenterName(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 180 }}
            allowClear
          />

          <Select
            placeholder="Filter by Status"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 130 }}
            allowClear
          >
            <Option value="all">All Statuses</Option>
            <Option value="PENDING">PENDING</Option>
            <Option value="DELIVERED">DELIVERED</Option>
            <Option value="FAILED">FAILED</Option>
            <Option value="REJECTED">REJECTED</Option>
            <Option value="NO_ROUTE">NO_ROUTE</Option>
          </Select>

          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates || [null, null])}
            style={{ width: 230 }}
          />

          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Search
          </Button>

          <Button
            icon={<ClearOutlined />}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>

          <Button
            icon={<SyncOutlined />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Space>
      </div>
      {logs.error && (
        <div className="error-message">
          <p>{logs.error}</p>
        </div>
      )}
      <div className="admin-table-container">
        <Table
          columns={columns}
          dataSource={logs.data}
          loading={logs.loading}
          size="small"
          rowKey="hol_id"
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: logs.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} logs`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  );
};

export default IntegrationLogs;
